import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
  HttpResponse,
  HttpStatusCode
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  Observable,
  catchError,
  finalize,
  map,
  of,
  shareReplay,
  switchMap,
  take,
  tap,
  throwError
} from 'rxjs';

import { ConsentFormService } from '@consent/services/consent-form.service';
import { CACHE_API } from '@constants/api.constants';
import { TOASTER_TYPE } from '@constants/app.enums';
import { ROUTES } from '@constants/routes.enums';
import { environment } from '@environment/environment';
import { AuthService } from '@services/auth.service';
import { LoggerService } from '@services/logger.service';
import { SessionTimeoutService } from '@services/session-timeout.service';
import { StorageService } from '@services/storage.service';
import { ToasterService } from '@services/toaster.service';
import { UserStateService } from '@services/user-state.service';

// ──────────────────────────────────────────────────────────────────────────────
// Constants
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Non-standard HTTP status the backend returns when a session is invalidated
 * server-side (admin force-logout, idle expiry, or "revoke all sessions").
 * It is not part of {@link HttpStatusCode}, so it is declared explicitly.
 */
const SESSION_TERMINATED_STATUS = 440;

/**
 * HTTP 451 (Unavailable For Legal Reasons) — the backend's consent gate: it
 * refuses data/feature endpoints until the user has accepted the mandatory
 * consent. Not part of {@link HttpStatusCode}, so declared explicitly. The
 * user-facing remedy is the consent dialog, not a toast — see
 * {@link handleConsentRequired}.
 */
const CONSENT_REQUIRED_STATUS = 451;

/** Backend `error_code` raised when refresh-token replay/theft is detected. */
const TOKEN_THEFT_ERROR_CODE = 'TOKEN_THEFT_DETECTED';

/** Toast shown when the backend reports refresh-token theft. */
const TOKEN_THEFT_MESSAGE =
  'Security alert: all sessions were terminated due to suspicious activity.';

/**
 * Window during which the global 401 toast + redirect is suppressed. Prevents a
 * burst of simultaneous 401s from stacking duplicate toasts and redirects.
 */
const UNAUTHORIZED_DEBOUNCE_MS = 3000;

/**
 * How long error toasts are muted after a session-ending error (440 / 401 /
 * token-theft). The interceptor surfaces a single dialog or logout redirect; this
 * window swallows the component-level "Failed to load X" toasts that fire on the
 * same burst of failed parallel requests, so they don't stack over the dialog.
 */
const SESSION_ERROR_MUTE_MS = 3000;

/**
 * Request path fragments for session-termination endpoints. A 404 from these is
 * expected (the session was already gone) and must not surface an error toast —
 * components handle it silently by removing the row from their local list.
 */
const SESSION_TERMINATE_PATHS = [
  '/auth/sessions/',
  '/admin/sessions/'
] as const;

// ──────────────────────────────────────────────────────────────────────────────
// Token interceptor — attaches auth + session headers to every authenticated request.
// ──────────────────────────────────────────────────────────────────────────────

export const HttpTokenInterceptor: HttpInterceptorFn = (request, next) => {
  const userStateService = inject(UserStateService);
  const token = userStateService.getToken();

  // No token → anonymous request (login, password reset, etc.). Pass through untouched.
  if (!token) {
    return next(request);
  }

  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    'X-Timezone-Offset': String(new Date().getTimezoneOffset())
  };

  const sessionId = userStateService.getSessionId();
  if (sessionId) {
    headers['X-Session-ID'] = sessionId;
  }

  return next(request.clone({ setHeaders: headers }));
};

// ──────────────────────────────────────────────────────────────────────────────
// Error interceptor — maps HTTP errors to the right user-facing reaction.
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Debounces the 401 toast + redirect across simultaneous request failures.
 * Module-level on purpose: the flag must be shared by every request that passes
 * through the interceptor, not reset per-request.
 */
let isUnauthorizedToastShown = false;

/**
 * Debounces the consent-required (451) reaction across a burst of simultaneous
 * failures. Module-level on purpose: the page, header and widget requests all
 * 451 at once, and only the FIRST should mute the toasts + kick the consent
 * flow. Reset after {@link CONSENT_REQUIRED_DEBOUNCE_MS}.
 */
let isConsentRequiredHandled = false;
const CONSENT_REQUIRED_DEBOUNCE_MS = 3000;

interface ErrorHandlerDeps {
  router: Router;
  toasterService: ToasterService;
  sessionTimeoutService: SessionTimeoutService;
  consentFormService: ConsentFormService;
  authService: AuthService;
  userStateService: UserStateService;
}

export const HttpErrorInterceptor: HttpInterceptorFn = (request, next) => {
  const deps: ErrorHandlerDeps = {
    router: inject(Router),
    toasterService: inject(ToasterService),
    sessionTimeoutService: inject(SessionTimeoutService),
    consentFormService: inject(ConsentFormService),
    authService: inject(AuthService),
    userStateService: inject(UserStateService)
  };

  return next(request).pipe(
    catchError((error: HttpErrorResponse) =>
      handleHttpError(error, request, next, deps)
    )
  );
};

/**
 * Single in-flight refresh, shared across every request that 401s in the same
 * burst — without this, N parallel failed requests would each fire their own
 * /auth/refresh call.
 */
let refreshInFlight$: Observable<string> | null = null;

/**
 * On a standard 401, try ONE silent token refresh (FS-08) before falling back
 * to the existing logout flow. The refresh call itself goes through
 * AuthService.refreshAccessToken(), which uses noInterceptorHttp — so a
 * refresh failure never re-enters this interceptor and can't loop.
 */
function attemptRefreshAndRetry(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
  originalError: HttpErrorResponse,
  deps: ErrorHandlerDeps
): Observable<HttpEvent<unknown>> {
  const {
    router,
    toasterService,
    sessionTimeoutService,
    authService,
    userStateService
  } = deps;

  if (!refreshInFlight$) {
    refreshInFlight$ = authService.refreshAccessToken().pipe(
      map((res) => res.data.access_token),
      shareReplay(1),
      finalize(() => {
        refreshInFlight$ = null;
      })
    );
  }

  return refreshInFlight$.pipe(
    switchMap((newToken) => {
      // refreshAccessToken() may have rotated the session id (see
      // AuthService.refreshAccessToken) — re-read it from UserStateService
      // rather than copying the pre-refresh value off the original request,
      // so the retried call doesn't carry a stale X-Session-ID.
      const headers: Record<string, string> = {
        Authorization: `Bearer ${newToken}`
      };
      const sessionId = userStateService.getSessionId();
      if (sessionId) {
        headers['X-Session-ID'] = sessionId;
      }
      return next(request.clone({ setHeaders: headers }));
    }),
    catchError(() => {
      // Refresh itself failed (or the retried request 401s again) — this is a
      // real session end, not a one-off token race. Fall back to logout.
      handleUnauthorized(
        originalError,
        router,
        toasterService,
        sessionTimeoutService
      );
      return throwError(() => logAndNormalize(originalError));
    })
  );
}

/**
 * Routes an {@link HttpErrorResponse} to its handler in priority order
 * (most severe / most specific first) and always rethrows so callers can react.
 */
function handleHttpError(
  error: HttpErrorResponse,
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
  deps: ErrorHandlerDeps
): Observable<HttpEvent<unknown>> {
  const { router, toasterService, sessionTimeoutService } = deps;

  // startsWith (not ===) so query params like ?returnUrl=… still match.
  const isOnLoginPage = router.url.startsWith(ROUTES.LOGIN);

  // 440 — session terminated server-side. Delegate cleanup + dialog to the
  // SessionTimeoutService so the duplicate-dialog guard lives in one place.
  if (error.status === SESSION_TERMINATED_STATUS && !isOnLoginPage) {
    // Mute the burst of component "Failed to load X" toasts from the other
    // requests that failed alongside this one — the dialog is the only notice.
    toasterService.muteErrors(SESSION_ERROR_MUTE_MS);
    sessionTimeoutService.clearAuthStorage();
    sessionTimeoutService.showSessionTerminatedDialog(error.error?.message);
    return throwError(() => error);
  }

  // 429 — rate limited. Show a dismissable dialog; do not log the user out.
  // Skip the global dialog for the login endpoint: the login component handles
  // session-limit 429s itself with the self-service session termination dialog.
  if (error.status === HttpStatusCode.TooManyRequests) {
    if (!request.url.includes('/auth/login')) {
      sessionTimeoutService.showRateLimitedDialog(error.error?.message);
    }
    return throwError(() => error);
  }

  // 401 + TOKEN_THEFT_DETECTED — refresh-token replay detected; all sessions revoked.
  if (isTokenTheft(error) && !isOnLoginPage) {
    // Mute component error toasts from the same failed-request burst, then force
    // this single security alert through the mute so it still reaches the user.
    toasterService.muteErrors(SESSION_ERROR_MUTE_MS);
    sessionTimeoutService.clearAuthStorage();
    // Close any open dialog (e.g. the consent modal) and reset consent state so
    // re-login starts clean, before redirecting to logout.
    sessionTimeoutService.tearDownDialogsAndConsent();
    toasterService.display(TOKEN_THEFT_MESSAGE, TOASTER_TYPE.ERROR, {
      force: true
    });
    router.navigate([ROUTES.LOGOUT]);
    return throwError(() => error);
  }

  // Standard 401 — token expired/invalid. Try one silent refresh (FS-08)
  // before falling back to the debounced toast + logout redirect.
  if (error.status === HttpStatusCode.Unauthorized && !isOnLoginPage) {
    return attemptRefreshAndRetry(request, next, error, deps);
  }

  // 451 — consent gate. The mandatory consent dialog is the user-facing remedy,
  // so swallow the burst of "consent required" toasts (the parallel page/header/
  // widget requests all 451 at once) and make sure the consent flow is running.
  if (error.status === CONSENT_REQUIRED_STATUS && !isOnLoginPage) {
    handleConsentRequired(request, deps);
    return throwError(() => logAndNormalize(error));
  }

  // Everything else — surface the backend message unless it's an expected
  // 404 from a session-terminate endpoint (session was already gone).
  if (error.error?.message && !isExpectedSessionTerminate404(error, request)) {
    toasterService.display(error.error.message, TOASTER_TYPE.ERROR);
  }
  return throwError(() => logAndNormalize(error));
}

/** True when the error is a 401 carrying the token-theft `error_code`. */
function isTokenTheft(error: HttpErrorResponse): boolean {
  return (
    error.status === HttpStatusCode.Unauthorized &&
    error.error?.error_code === TOKEN_THEFT_ERROR_CODE
  );
}

/**
 * Handles a standard 401: shows the backend message (once), redirects to logout,
 * and debounces both for {@link UNAUTHORIZED_DEBOUNCE_MS} so a burst of parallel
 * 401s doesn't stack duplicate toasts/redirects.
 */
function handleUnauthorized(
  error: HttpErrorResponse,
  router: Router,
  toasterService: ToasterService,
  sessionTimeoutService: SessionTimeoutService
): void {
  if (isUnauthorizedToastShown) {
    return;
  }
  isUnauthorizedToastShown = true;

  // Mute the component error toasts from the parallel 401s, then force this one
  // backend message through so the user still sees why they were logged out.
  toasterService.muteErrors(SESSION_ERROR_MUTE_MS);
  if (error.error?.message) {
    toasterService.display(error.error.message, TOASTER_TYPE.ERROR, {
      force: true
    });
  }
  // Close any open dialog (e.g. the consent modal) and reset consent state so it
  // can't linger over the login screen and re-login re-prompts cleanly. Inside
  // the debounce guard, so a burst of parallel 401s only tears down once.
  sessionTimeoutService.tearDownDialogsAndConsent();
  router.navigate([ROUTES.LOGOUT]);

  setTimeout(() => {
    isUnauthorizedToastShown = false;
  }, UNAUTHORIZED_DEBOUNCE_MS);
}

/**
 * Reacts to a 451 (consent required). Debounced across the simultaneous-failure
 * burst so it runs once: mutes the component "Failed to load X" toasts (the
 * consent dialog is the only notice the user should see) and, as a safety net,
 * ensures the mandatory consent flow is running — covering deep-link/automation
 * navigation where the shell's own trigger may not have fired. Idempotent:
 * {@link ConsentFormService.ensureGranted} shares one in-flight resolution and
 * one dialog.
 *
 * Skips the consent service's own endpoints: those authenticate with an API key
 * and must never feed back into the consent resolution they drive (no loops).
 */
function handleConsentRequired(
  request: HttpRequest<unknown>,
  deps: ErrorHandlerDeps
): void {
  if (isConsentRequiredHandled) {
    return;
  }
  // A 451 from the consent service itself is not part of the app gate — don't
  // mute or re-trigger on it.
  if (
    environment.consentFormUrl &&
    request.url.startsWith(environment.consentFormUrl)
  ) {
    return;
  }
  isConsentRequiredHandled = true;

  deps.toasterService.muteErrors(SESSION_ERROR_MUTE_MS);
  deps.consentFormService.ensureGranted().pipe(take(1)).subscribe();

  setTimeout(() => {
    isConsentRequiredHandled = false;
  }, CONSENT_REQUIRED_DEBOUNCE_MS);
}

/** True for a 404 on a session-terminate endpoint, where the toast is suppressed. */
function isExpectedSessionTerminate404(
  error: HttpErrorResponse,
  request: HttpRequest<unknown>
): boolean {
  return (
    error.status === HttpStatusCode.NotFound &&
    SESSION_TERMINATE_PATHS.some((path) => request.url.includes(path))
  );
}

/**
 * Rebuilds a clean, predictable HttpErrorResponse (stripping transport noise),
 * logs it once, and returns it for rethrow.
 */
function logAndNormalize(error: HttpErrorResponse): HttpErrorResponse {
  const normalized = new HttpErrorResponse({
    error: error.error,
    statusText: error.message,
    status: error.status
  });
  LoggerService.error(normalized);
  return normalized;
}

// ──────────────────────────────────────────────────────────────────────────────
// Cache interceptor — serves whitelisted GETs from an in-memory cache.
// ──────────────────────────────────────────────────────────────────────────────

/** A request is cacheable when it's a GET against a whitelisted API base URL. */
function isCacheable(request: HttpRequest<unknown>): boolean {
  return (
    request.method === 'GET' &&
    CACHE_API.some((api) => request.url.startsWith(api))
  );
}

export const HttpCacheInterceptor: HttpInterceptorFn = (request, next) => {
  const storageService = inject(StorageService);

  if (isCacheable(request)) {
    const cachedResponse = storageService.cacheAPIs.get(request.url);
    if (cachedResponse) {
      return of(cachedResponse.clone());
    }
  }

  return next(request).pipe(
    tap((response) => {
      if (isCacheable(request) && response instanceof HttpResponse) {
        storageService.cacheAPIs.set(request.url, response.clone());
      }
    })
  );
};
